import React from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { getCustomerQuestions, sendAdminQuestion } from "../../store/AsyncMethods/ProductMethod";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";

export default function CustomerQuestion() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customerQuestions } = useSelector(state => state.ProductReducer);
  const [showReplyModal, setShowReplyModal] = React.useState(false);
  const [replyText, setReplyText] = React.useState("");
  const [activeQuestion, setActiveQuestion] = React.useState(null);

  useEffect(() => {
    dispatch(getCustomerQuestions());
  }, [dispatch]);

  // Dynamic summary stats
  const totalQuestions = customerQuestions.length;
  const answered = customerQuestions.filter(q => q.answer).length;
  const unanswered = totalQuestions - answered;
  // answered_in is assumed to be in seconds; filter only those with a value
  const answeredInArr = customerQuestions.map(q => Number(q.answered_in)).filter(Boolean);
  let avgResponse = 0;
  if (answeredInArr.length > 0) {
    avgResponse = answeredInArr.reduce((a, b) => a + b, 0) / answeredInArr.length;
  }
  // Format avg response time as Xh Ym or Xm
  let avgResponseStr = "-";
  if (avgResponse > 0) {
    const hours = Math.floor(avgResponse / 3600);
    const minutes = Math.round((avgResponse % 3600) / 60);
    avgResponseStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }
  const summaryStats = [
    { label: "Total Questions", value: totalQuestions },
    { label: "Unanswered", value: unanswered },
    { label: "Answered", value: answered },
    { label: "Avg. Response Time", value: avgResponseStr },
  ];

  const handleReplyClick = (rowData) => {
    setActiveQuestion(rowData);
    setReplyText("");
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!activeQuestion || !replyText.trim()) return;
    const payload = { id: activeQuestion.question_id, adminAnswer: replyText };
    const success = await dispatch(sendAdminQuestion(payload));
    if (success) {
      await dispatch(getCustomerQuestions());
      setShowReplyModal(false);
      setReplyText("");
      setActiveQuestion(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-gray-400 mb-1">Home &gt; Orders &gt; Customer Questions</div>
          <h2 className="text-2xl font-bold">Customer Questions</h2>
        </div>
        <Button label="Back" icon="pi pi-arrow-left" className="p-button-secondary" onClick={() => navigate(-1)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {summaryStats.map((stat, idx) => (
          <Card key={idx} className="shadow-sm rounded-xl" style={{ background: "#F7F8FA" }}>
            <div className="flex flex-col gap-1 items-center">
              <div className="text-gray-500 text-sm font-semibold mb-1">{stat.label}</div>
              <span className="text-3xl font-extrabold text-blue-600">{stat.value}</span>
            </div>
          </Card>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="font-bold text-lg mb-2">Questions</div>
        <DataTable value={customerQuestions} className="rounded-lg" showGridlines responsiveLayout="scroll">
          <Column field="product_id" header="Product ID" />
          <Column field="question" header="Question" />
          <Column field="customer_name" header="Customer" />
          <Column field="question_date" header="Date" body={rowData => {
            const date = new Date(rowData.question_date);
            if (isNaN(date)) return '-';
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          }} />
          <Column field="answer" header="Reply" body={rowData => rowData.answer ? rowData.answer : <span className="text-gray-400">-</span>} />
          <Column field="answer" header="Status" body={rowData => (
            <span className={rowData.answer ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>{rowData.answer ? "Answered" : "Unanswered"}</span>
          )} />
          <Column header="" body={rowData => (
            <Button label="Reply" link className="text-blue-600" onClick={() => handleReplyClick(rowData)} disabled={!!rowData.answer} />
          )} />
        </DataTable>
      </div>
      <Dialog header="Reply to Question" visible={showReplyModal} style={{ width: '40vw', maxWidth: 600 }} onHide={() => setShowReplyModal(false)} modal>
        {activeQuestion && (
          <div className="flex flex-col items-center">
            <div className="w-full text-center mb-2">
              <span className="font-bold">Customer Question</span>: {activeQuestion.question}
              <div className="text-xs text-gray-500 mt-1">Asked by: {activeQuestion.customer_name} on {activeQuestion.question_date ? new Date(activeQuestion.question_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
            </div>
            <div className="w-full">
              <InputTextarea
                autoResize
                value={replyText}
                onChange={e => setReplyText(e.target.value.slice(0, 500))}
                rows={3}
                maxLength={500}
                placeholder='Type your reply here...'
                className="w-full text-lg"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span></span>
                <span>{replyText.length}/500</span>
              </div>
            </div>
            <Button
              label="Reply"
              className="mt-4 w-full"
              disabled={!replyText.trim()}
              onClick={handleSendReply}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
} 